import type { ReactNode } from "react";
import { Link } from "@inertiajs/react";
import { tabsListVariants, tabsTriggerVariants } from "@/components/ui/Tabs";
import { cn } from "@/lib/utils";
import type { ScopeNode } from "@/components/ChannelMenu";
import { leafChannelsInTreeOrder } from "../utils/leafChannels";

interface Props {
  scopeTree: ScopeNode[];
  currentNodeId: string;
  // The page URL for a channel — show and edit pages link to themselves.
  hrefForChannel: (slug: string) => string;
  // Controls right after the tabs (By Day's day-window stepper).
  leading?: ReactNode;
  // Right-aligned controls on the same row (search box, Actions menu).
  trailing?: ReactNode;
}

// Sale-channel tabs row above the By Month grid, same look as the pricing
// page tabs. URL-driven: switching is a plain Inertia visit that keeps the
// scroll position.
export default function ByMonthChannelTabs({ scopeTree, currentNodeId, hrefForChannel, leading, trailing }: Props) {
  const channels = leafChannelsInTreeOrder(scopeTree);

  return (
    <nav
      aria-label="Sale channel"
      className={cn(tabsListVariants({ variant: "nav" }), "flex w-full items-center overflow-x-auto overflow-y-hidden px-2")}
    >
      {channels.length === 0 ? (
        <span className="px-3 py-2 text-[13px] text-muted-foreground">No channels</span>
      ) : (
        channels.map((ch) => {
          const active = ch.id === currentNodeId;
          return (
            <Link
              key={ch.id}
              href={hrefForChannel(ch.slug)}
              preserveScroll
              aria-current={active ? "page" : undefined}
              data-state={active ? "active" : "inactive"}
              className={cn(tabsTriggerVariants({ variant: "nav" }), "mb-0 no-underline")}
            >
              {ch.name}
            </Link>
          );
        })
      )}
      {leading && <div className="flex shrink-0 items-center pl-3">{leading}</div>}
      {trailing && <div className="ml-auto flex shrink-0 items-stretch self-stretch pl-3">{trailing}</div>}
    </nav>
  );
}
